/**
 * Merging two copies of the persisted state.
 *
 * This is the part of sync that has to be right. The server refuses a push
 * built on a stale read and hands back the current document; this decides what
 * the combined truth is. Get it wrong and the visible symptom is a watch list
 * that quietly loses entries — the kind of bug people notice weeks later and
 * cannot describe.
 *
 * The rules, and why:
 *
 * 1. **Watched entries union, never subtract.** If either side has watched
 *    something, the merge has watched it. Deletion by absence is impossible to
 *    tell apart from "this device never knew about it", and between wrongly
 *    keeping a tick and wrongly losing one, keeping is far less annoying —
 *    unticking is one click, reconstructing a history is not.
 *
 *    A deliberate un-watch is not an absence: it leaves a tombstone, and rule
 *    5 lets it win.
 *
 * 2. **Within an entry, the later `watchedAt` wins**, with the local copy
 *    breaking exact ties. This is per-entry rather than per-document, so two
 *    devices watching different films offline both keep their work.
 *
 * 3. **A rating beats no rating.** Someone bothered to set it, and null is the
 *    absence of an opinion rather than an opinion. Only when both sides have
 *    one does the later record's rating win.
 *
 * 4. **Settings take the local side.** They describe the device you are
 *    holding — theme, hours per week, whether Encore mode is on — and having
 *    another device reach over and change them is worse than them being
 *    slightly out of step.
 *
 * 5. **A tombstone beats a watch recorded earlier.** Un-ticking is an explicit
 *    act and has to survive, or the untick is silently undone by the next sync
 *    from a device that still has the entry.
 *
 *    Ordering the two is inexact, because a watch stores a local date with no
 *    time while a deletion stores a precise instant. The comparison is against
 *    the *start* of the watched day, so a deletion made at any point on or
 *    after that day wins. That resolves the common case correctly — tick
 *    something by mistake, untick it minutes later, sync — at the cost of the
 *    rare one: two devices, same day, one watching and one un-watching, where
 *    the un-watch wins regardless of which happened first. Re-ticking is one
 *    click; an untick that will not stick is a bug people cannot describe.
 */

import type { PersistedState, Tombstones, WatchRecord } from "@schema/persist.ts";

export interface MergeResult {
  state: PersistedState;
  /** For telling the reader what happened, rather than merging in silence. */
  added: number;
  updated: number;
  /** Entries an un-watch on another device removed here. */
  removed: number;
}

/**
 * How long an un-watch is remembered.
 *
 * Tombstones cannot accumulate forever — every un-tick would live in the
 * payload for good. Ninety days matches the session lifetime, which bounds how
 * far behind a device can realistically be: one that has not synced in longer
 * has to sign in again anyway, and its stale copy loses to the server's.
 *
 * Purged during merge rather than on a timer, because merge is the only place
 * that runs on every device and knows both sides.
 */
const TOMBSTONE_TTL_MS = 90 * 24 * 60 * 60 * 1000;

export function mergeStates(
  local: PersistedState,
  remote: PersistedState,
  now: number = Date.now(),
): MergeResult {
  const watched: Record<string, WatchRecord> = { ...remote.watched };
  // Both sides' tombstones, latest un-watch per entry.
  const deleted: Tombstones = { ...remote.deleted };
  for (const [id, at] of Object.entries(local.deleted)) {
    const theirs = deleted[id];
    if (theirs === undefined || at > theirs) deleted[id] = at;
  }
  let added = 0;
  let updated = 0;
  let removed = 0;

  for (const [id, mine] of Object.entries(local.watched)) {
    const theirs = watched[id];
    if (!theirs) {
      watched[id] = mine;
      added += 1;
      continue;
    }
    const winner = pickRecord(mine, theirs);
    watched[id] = winner;
    // Compared by value, not by identity. `theirs` was parsed from JSON and
    // `mine` from localStorage, so they are never the same object even when
    // they say the same thing — and ties go to the local copy, which made
    // every unchanged entry count as an update. The symptom was a second sync
    // of untouched data reporting "81 updated", which reads as sync rewriting
    // your list on every run.
    if (!sameRecord(winner, theirs)) updated += 1;
  }

  // Entries only the remote had are already in `watched` from the spread, and
  // are counted as additions from this device's point of view.
  for (const id of Object.keys(remote.watched)) {
    if (!(id in local.watched)) added += 1;
  }

  // Rule 5, applied last so it overrides the union above.
  for (const [id, deletedAt] of Object.entries(deleted)) {
    const record = watched[id];
    if (record && deletedAt >= startOfWatchedDay(record.watchedAt)) {
      delete watched[id];
      removed += 1;
      // It was counted as added or updated a moment ago; it is neither.
      if (id in local.watched && id in remote.watched) updated = Math.max(0, updated - 1);
      else added = Math.max(0, added - 1);
    }
  }

  // Forget un-watches nobody can still be arguing about. Done after rule 5 so
  // a tombstone always gets to act before it is dropped.
  const cutoff = now - TOMBSTONE_TTL_MS;
  for (const [id, at] of Object.entries(deleted)) {
    if (at < cutoff) delete deleted[id];
  }

  return {
    state: {
      v: local.v,
      watched,
      deleted,
      // Rule 4: this device's settings survive a merge.
      settings: local.settings,
    },
    added,
    updated,
    removed,
  };
}

/** Midnight of a `YYYY-MM-DD` watch date, in epoch ms — see rule 5. */
function startOfWatchedDay(watchedAt: string): number {
  const t = Date.parse(`${watchedAt}T00:00:00Z`);
  // An unparseable date should not silently delete someone's entry.
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/** Value equality over every field that survives a merge. */
function sameRecord(a: WatchRecord, b: WatchRecord): boolean {
  return (
    a.watchedAt === b.watchedAt &&
    a.rating10 === b.rating10 &&
    a.rewatch === b.rewatch &&
    a.source === b.source
  );
}

/**
 * Whether two documents say the same thing about what has been watched.
 *
 * Settings are excluded on purpose: they are local by rule 4, so a device
 * whose theme differs from the server's copy has nothing to upload.
 *
 * Tombstones are NOT excluded, and leaving them out was a bug worth naming:
 * this is what decides whether a sync bothers pushing at all, and a device
 * whose only change is an un-watch has identical `watched` maps on both sides
 * — the entry is gone from one and about to be removed from the other. Compare
 * only `watched` and that device concludes it has nothing to say, skips the
 * push, and the deletion never reaches anywhere else.
 */
export function sameWatched(a: PersistedState, b: PersistedState): boolean {
  const ids = Object.keys(a.watched);
  if (ids.length !== Object.keys(b.watched).length) return false;
  const sameRecords = ids.every((id) => {
    const other = b.watched[id];
    const mine = a.watched[id];
    return mine !== undefined && other !== undefined && sameRecord(mine, other);
  });
  if (!sameRecords) return false;

  const tombs = Object.keys(a.deleted);
  if (tombs.length !== Object.keys(b.deleted).length) return false;
  return tombs.every((id) => a.deleted[id] === b.deleted[id]);
}

/**
 * The later watch wins; a rating beats no rating regardless.
 *
 * Splitting these matters: a device that re-watched something later but never
 * rated it should not erase a rating set elsewhere. The result can therefore
 * be a record neither side had — the newer watch date with the surviving
 * rating — which is the honest combination rather than a choice between them.
 */
function pickRecord(mine: WatchRecord, theirs: WatchRecord): WatchRecord {
  const newer = mine.watchedAt >= theirs.watchedAt ? mine : theirs;
  const older = newer === mine ? theirs : mine;

  const rating10 = newer.rating10 ?? older.rating10;
  const rewatch = mine.rewatch || theirs.rewatch;

  if (newer.rating10 === rating10 && newer.rewatch === rewatch) return newer;
  return { ...newer, rating10, rewatch };
}
